/**
 * 「语文听写」工作区的 **tab 清单与顺序** + 「整块导入（高级）」的**显示开关**
 * （纯常量 / 纯函数，有单测）。
 *
 * ## 为什么 tab 清单搬到这里
 *
 * 本轮连着改了两件事，而它们都是"**顺序 / 有无**"这种一改就得两边对齐的事：
 *
 *   1. 用户要求把三个标签从「按书录入 | 整块导入（高级） | 浏览与编辑」
 *      改成「浏览与编辑 | 按书录入 | 整块导入（高级）」；
 *   2. 紧接着用户又说："把'整块导入（高级）'这个功能去掉。"
 *
 * 顺序与"有没有这个 tab"散在 JSX 里时，测试只能靠读源码字符串来守 —— 很容易
 * 出现"测试说两个、界面其实三个"。所以这里把清单做成**数据**：
 * `DictationWorkspace` 只负责渲染它，测试直接断言返回值。
 *
 * | tab | 谁有 | 标签 |
 * | --- | --- | --- |
 * | `browse` | 两种模式 | 浏览与编辑 / 浏览字词 |
 * | `books` | 两种模式 | 按书录入 / 按书看字表 |
 * | `import` | **只有维护模式**，而且**默认隐藏**（见下面那个开关） | 整块导入（高级） |
 * | `pick` | 只有家长模式 | 选词预览 |
 *
 * ## ⚠️ 默认标签**不跟着顺序走**
 *
 * 用户原话："默认选中的标签保持不变"。所以默认页单独一个常量
 * （`DEFAULT_DICTATION_TAB = 'books'` = **按书录入**）：把「浏览与编辑」排到第一位
 * **不是**为了让它当默认页 —— 进这一页要做的第一件事仍然是"选一本书"。
 * ⛔ 不要为了"第一个 tab 应该默认选中"去改它。
 */
export type DictationTabKey = 'books' | 'import' | 'browse' | 'pick';

export interface DictationTabItem {
  key: DictationTabKey;
  label: string;
  hint: string;
}

/**
 * 进这一页默认停在哪个 tab。
 *
 * ## 2026-11-04 变更（用户新要求）
 *
 * > "点击「听写字表」后，默认显示「浏览与编辑」。"
 *
 * 所以**管理后台**（维护模式）默认 = `browse`（浏览与编辑）。
 *
 * ⚠️ 家长端**不变**，仍是 `books`（按书看字表）—— 家长进这一页要做的第一件事
 *    就是"选一本书看字"，他没有"编辑"这个概念；当年"默认选中保持不变"那句
 *    要求针对的也是家长端那一屏。
 *
 * ⛔ 别把它改回单一常量：这一行是"两个角色、两种默认页"的**唯一**出处，
 *    写成常量就会出现"改了后台把家长端也改了"。
 */
export function defaultDictationTab(maintain: boolean): DictationTabKey {
  return maintain ? 'browse' : 'books';
}

/** @deprecated 用 `defaultDictationTab(maintain)`：默认页现在**按角色**不同。 */
export const DEFAULT_DICTATION_TAB: DictationTabKey = 'browse';

/**
 * 「整块导入（高级）」这个 tab 现在渲不渲染。
 *
 * ## 用户原话（2026-10-18）
 *
 * > 把"整块导入（高级）"这个功能去掉。
 *
 * ## ⛔ 这个开关**只**管前端入口，不碰数据、不碰后端
 *
 *   · 后端端点**全部保留**（一个路由都没动）：`POST /admin/dictation/lists/import`
 *     正是「按书录入」在用的那条（"把这一张表粘进去"），删它等于把录字表也弄坏；
 *   · 组件（`DictationImportPanel` / `lib/dictationImport.ts` 的状态机 / 解析器）
 *     **都在原处**，只是不渲染；
 *   · "按书录入"照样能建课本、建课文、录三张表 —— 这条路一个字都没改。
 *
 * ## 🔁 恢复办法：把下面这一行改成 `true`
 *
 * 改完这一处，以下界面会一起回来（它们全部读这个常量，没有第二处开关）：
 *   · 「整块导入（高级）」那个 tab（排在「按书录入」后面）；
 *   · 那个 tab 的面板（`DictationImportPanel`）；
 *   · 一条课本都没有时「去整块导入建课本」那个动作按钮，以及那句提到它的指引。
 *
 * 这就是为什么这里不做"物理删除"：恢复只该是一次一行的改动。
 */
export const DICTATION_WHOLE_IMPORT_UI_ENABLED = false;

/**
 * 界面现在要不要渲染「整块导入」相关的东西。
 *
 * 参数只有一个用途：**单测**要能验证开关两个方向的行为（`true` / `false`），
 * 而生产调用处一律不传参（用上面的常量）。
 */
export function wholeImportUiEnabled(
  enabled: boolean = DICTATION_WHOLE_IMPORT_UI_ENABLED,
): boolean {
  return enabled;
}

/**
 * 这一屏的 tab 清单（顺序就是界面上从左到右的顺序）。
 *
 * @param input.maintain 系统管理员（`/admin/dictation`）为 `true`；家长为 `false`
 * @param input.browseHint 浏览 tab 副标题的前半句（课文隐藏时是"按课本/年级看"）
 * @param input.wholeImportEnabled 省略 = 用上面的常量（单测里可以显式传两个方向）
 */
export function dictationTabs(input: {
  maintain: boolean;
  browseHint: string;
  wholeImportEnabled?: boolean;
}): DictationTabItem[] {
  const { maintain, browseHint } = input;
  const wholeImport = wholeImportUiEnabled(
    input.wholeImportEnabled ?? DICTATION_WHOLE_IMPORT_UI_ENABLED,
  );

  if (!maintain) {
    /**
     * 家长端只有"看"（外加选词预览）：**没有**整块导入，也没有"按书录入"这个说法
     * （他录不了字表）。顺序没变 —— 本轮用户要求调顺序的是管理后台那三个。
     */
    return [
      /**
       * ⚠️ 2026-10-18（本轮修正）：这一屏原来有一块"这一张表里已有什么"的字卡网格，
       *    上一轮**误删**了（用户说的是"管理员端录入字词那一块不用再显示字词"）。
       *    现在它**回来了**，而且是新网格（拼音在上、点字卡开浮窗）。
       *    副标题必须与界面对得上：先选书 → 看三张表各多少 → 点字卡看读音。
       */
      { key: 'books', label: '按书看字表', hint: '选一本书 → 三张表各多少，点字卡看读音' },
      { key: 'browse', label: '浏览字词', hint: `${browseHint}，能查多音字` },
      { key: 'pick', label: '选词预览', hint: '按表和读音选，看会取到哪些词' },
    ];
  }

  const tabs: DictationTabItem[] = [
    /**
     * ⚠️ 2026-10-18 本轮：副标题从"点拼音纠音"改成"点字卡看读音/造词" ——
     *    字卡改成网格之后，**拼音不再是可点的按钮**：点整张卡片会弹出浮窗，
     *    听写读音、造词、标注多音字、改拼音、删除全在里面。
     */
    { key: 'browse', label: '浏览与编辑', hint: `${browseHint}，点字卡看读音、造词` },
    { key: 'books', label: '按书录入', hint: '选一本书 → 识字表/写字表/词语表各录各的' },
  ];
  /**
   * ⚠️ 整块导入排在**最后**：恢复它时顺序仍是用户要的
   *    「浏览与编辑 | 按书录入 | 整块导入（高级）」。
   */
  if (wholeImport) {
    tabs.push({
      key: 'import',
      label: '整块导入（高级）',
      hint: '一份文本里什么都能有的通用入口',
    });
  }
  return tabs;
}
